/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { Markup } from "interweave";
import { useTranslation } from "react-i18next";

/** Definintion of resource type icons */
import { ReactComponent as BLCircles } from "../../../assets/icons/resource-types/blended-learning.svg";
import { ReactComponent as BLPLus } from "../../../assets/icons/resource-types/blended-learning-plus.svg";
import { ReactComponent as ClassRoom } from "../../../assets/icons/resource-types/classroom-resource.svg";
import { ReactComponent as DiagnosticTool } from "../../../assets/icons/resource-types/diagnostic-tool.svg";
import { ReactComponent as ExamWalkThrough } from "../../../assets/icons/resource-types/exam-walk-through.svg";
import { ReactComponent as KnowledgeOrganiser } from "../../../assets/icons/resource-types/knowledge-organiser.svg";
import { ReactComponent as LessonPlan } from "../../../assets/icons/resource-types/lesson-plan.svg";
import { ReactComponent as ExamReview } from "../../../assets/icons/resource-types/online-exam-review.svg";
import { ReactComponent as OtherResources } from "../../../assets/icons/resource-types/other-resources.svg";
import { ReactComponent as QuestionBank } from "../../../assets/icons/resource-types/question-bank.svg";
import { ReactComponent as StudentPlanner } from "../../../assets/icons/resource-types/student-planner.svg";
import { ReactComponent as WelshGovLogo } from "../../../assets/images/welsh-gov-logo.svg";
import { ReactComponent as AdnoddLogo } from "../../../assets/images/adnodd-colour.svg";
import { ReactComponent as AdnoddLogoMobile } from "../../../assets/images/adnodd-mobile.svg";
import { ReactComponent as Book } from "bootstrap-icons/icons/book.svg";
import { Link } from "react-router-dom";
import StarResource from "../../starResource/StarResource";
import { ResourceReference, Spec, SpecItemData } from "../../../types";
import { useLocale } from "../../../i18n/hooks";
import "./ResourceDetails.scss"

type Props = {
  resource: ResourceReference;
  star?: boolean;
};

const ResourceDetails: React.FC<Props> = ({ resource }) => {
  const { t: _ } = useTranslation();
  const [starred, setStarred] = useState(true);
  const [specItem, setSpecItem] = useState<SpecItemData>();
  const resourceObject = resource.Object;
  const locale = useLocale();

  const generateSpecBreadcrumb = (
    SpecItem: Spec[],
    index: string[],
    currSpecIndex?: string,
  ): React.ReactElement => {
    let _index = [...index];
    const item = Number(_index.pop() ?? "-1");

    if (item < 0) return <></>;

    let _currSpecIndex = currSpecIndex
      ? `${currSpecIndex}.${item}`
      : item.toString();

    const currSpec = SpecItem[item];

    if (_index?.length > 0 && currSpec.children) {
      return (
        <>
          <Link
            className="text-secondary"
            to={`../?qualification=${resource.Qualification}&spec=${_currSpecIndex}`}
          >
            {currSpec.title[locale]}
          </Link>{" "}
          &gt;{" "}
          {generateSpecBreadcrumb(currSpec.children, _index, _currSpecIndex)}
        </>
      );
    } else {
      return (
        <Link
          className="text-secondary"
          to={`../?qualification=${resource.Qualification}&spec=${_currSpecIndex}`}
        >
          {currSpec.title[locale]}
        </Link>
      );
    }
  };

  const getSpecItems = useCallback(async () => {
    if (
      resource.Qualification !== undefined &&
      resource?.Subject?.id !== undefined
    ) {
      try {
        let formattedQual = resource.Qualification.replace(
          /([a-z0-9])([A-Z])/g,
          "$1-$2",
        ).toLowerCase();
        let formattedSubject = resource.Subject.id.toLowerCase();
        const data = require(
          `../../../json/specItems/${formattedQual}-${formattedSubject}.json`,
        );
        setSpecItem(data);
      } catch (e) {
        setSpecItem(undefined);
      }
    }
  }, [resource]);

  useEffect(() => {
    getSpecItems();

    return () => {
      setSpecItem(undefined);
    };
  }, [resource, getSpecItems]);

  return (
    <div className="resource-details">
      <div className="resource-identifiers mb-4 mt-2 d-flex gap-4 align-items-center">
        <div id="resource-labels" className="me-auto">
          {resourceObject.levels &&
            resourceObject.levels.map((level: string) => (
              <Link to={`../?level=${level}`} key={level}>
                <small
                  className={`bg-dark d-inline-block px-2 mt-2 rounded shadow-sm text-white bg-${level.toLowerCase()}`}
                >
                  {_(level)}
                </small>
                <br />
              </Link>
            ))}
          {/* {resource.categories.map((cat: string) => (
            <Link to={`../?cat=${cat}`} key={cat}>
              <small
                className={`text-capitalize bg-light d-inline-block px-2 mt-2 rounded shadow-sm text-dark bg-${cat
                  .replace(' ', '-')
                  .toLowerCase()}`}
              >
                {cat}
              </small>
              <br />
            </Link>
          ))} */}
        </div>
        <div
          className="display-6 star-container d-flex"
          role="button"
          onClick={() => {
            setStarred(!starred);
          }}
        >
          <StarResource
            isStarred={starred}
            onClick={() => {
              setStarred(!starred);
            }}
          />
        </div>

        {resourceObject.fundedBy === "WelshGov" && (
          <div className="logo">
            <a target="_blank" rel="noreferrer" href="https://www.gov.wales/">
              <WelshGovLogo />
            </a>
          </div>
        )}

        {resourceObject.fundedBy === "Adnodd" && (
          <div className="logo">
            <a
              target="_blank"
              rel="noreferrer"
              href={
                locale === "en"
                  ? "https://adnodd.gov.wales/"
                  : "https://adnodd.llyw.cymru/"
              }
            >
              <AdnoddLogo className="d-none d-sm-block" />
              <AdnoddLogoMobile className="d-block d-sm-none" />
            </a>
          </div>
        )}

        {/* <ResourceIcon
        type={resource.Type} 
        />  */}
        <div id="resource-type" className="display-2 d-flex resource-icon">
          {resource.Type === "BL" &&
            (resource.PK.includes("USER:") ? (
              <BLPLus
                className="bi blplus"
                title={_("Blended Learning Plus")}
              />
            ) : (
              <BLCircles className="bi" title={_("Blended Learning")} />
            ))}
          {resource.Type === "CR" && (
            <>
              <ClassRoom className="bi" title={_("Classroom Resource")} />
            </>
          )}
          {resource.Type === "DT" && (
            <>
              <DiagnosticTool className="bi" title={_("Diagnostic Tool")} />
            </>
          )}
          {resource.Type === "EWT" && (
            <>
              <ExamWalkThrough className="bi" title={_("Exam Walk Through")} />
            </>
          )}
          {resource.Type === "KO" && (
            <>
              <KnowledgeOrganiser
                className="bi"
                title={_("Knowledge Organiser")}
              />
            </>
          )}
          {resource.Type === "LP" && (
            <>
              <LessonPlan className="bi" title={_("Lesson Plan")} />
            </>
          )}
          {resource.Type === "OER" && (
            <>
              <ExamReview className="bi" title={_("Online Exam Review")} />
            </>
          )}
          {resource.Type === "OR" && (
            <>
              <OtherResources className="bi" title={_("Other Resources")} />
            </>
          )}
          {resource.Type === "QB" && (
            <>
              <QuestionBank className="bi" title={_("Question Bank")} />
            </>
          )}
          {resource.Type === "SP" && (
            <>
              <StudentPlanner className="bi" title={_("Student Planner")} />
            </>
          )}
        </div>
      </div>
      <div className="d-flex gap-4 align-items-center mb-2">
        <Markup tagName="h1" content={resourceObject.title} />
      </div>
      {specItem && (
        <p className="text-secondary mt-2">
          <Book className="me-2" />
          <small>
            {generateSpecBreadcrumb(
              specItem.spec,
              resource.SpecItem.split(".")?.reverse(),
            )}
          </small>
        </p>
      )}
      <Markup tagName="div" content={resourceObject.description} />
    </div>
  );
};

export default ResourceDetails;
